import marked from 'marked';
import translate from './translate';

// filter/find helper functions
// examples:
//   organizationData.webPages.filter(finnish)
//   data.serviceNames.find(finnishWithType('Name'))
//   services.filter(inOntologies(activeOntologies))
import {
  lifeEventsFilters,
  ontologyFilters,
  serviceClassFilters,
  targetGroupFilters,
} from '../filters.js';

import { pharmacyMunicipalityMap } from '../constants.js';

export const finnish = (item) => item.language === 'fi';

export const finnishWithType = (type) => (item) =>
  finnish(item) && item.type === type;

export const inOntologies = (ontologies) => (service) =>
  service.ontologyTerms?.some((term) =>
    ontologies.some(
      (item) => item === term.name.find(finnish)?.value,
    ),
  );

export const inServiceClass = (validServiceClasses) => (
  service,
) =>
  service.serviceClasses?.some((term) =>
    validServiceClasses.includes(term.code),
  );

export const inTargetGroup = (validTargetGroup) => (
  service,
) =>
  service.targetGroups?.some((term) =>
    validTargetGroup.includes(term.code),
  );

export const inLifeEvent = (validLifeEvent) => (service) =>
  service.lifeEvents?.some(
    (term) => validLifeEvent === term.code,
  );

// Shortens input string into threshold length and adds '…' if input longer than threshold
export const trimmed = (input = '', threshold = 150) =>
  input.length > threshold
    ? `${input.slice(0, threshold - 2)}…`
    : input;

// doesn't return empty arrays
export const getItemsFromServiceTemplate = (services) =>
  Object.values(services)
    .flatMap((item) => Object.values(item))
    .flat();

// eg. 08:30:00 => 8.30
const prettifyTime = (input) => {
  if (!input) return null;
  const [hour, min] = input.split(':');
  return `${parseInt(hour)}:${min}`;
};

// eg. "2021-06-01T00:00:00 => 1.6.2021
const prettifyDate = (input) => {
  try {
    const date = new Date(input);
    return `${date.getDate()}.${
      date.getMonth() + 1
    }.${date.getFullYear()}`;
  } catch (err) {
    return null;
  }
};

// Data normalizers

const normalizeAddress = (data) => {
  const addressData =
    data.streetAddress || data.postOfficeBoxAddress;
  const streetOrBox =
    addressData?.street?.find(finnish)?.value ||
    addressData?.postOfficeBox?.find(finnish)?.value ||
    '';
  const streetNumber = addressData?.streetNumber || '';
  const postalCode = addressData?.postalCode || '';
  const postOffice =
    addressData?.postOffice?.find(finnish)?.value || '';

  const address = `${streetOrBox}${
    streetNumber && ' ' + streetNumber
  }, ${postalCode} ${postOffice}`;

  return {
    type: data.type,
    subType: data.subType,
    address,
  };
};

const normalizePhoneNumber = (data) => {
  return {
    type: data.type,
    info: data.additionalInformation,
    serviceChargeType: data.serviceChargeType,
    // some numbers are missing the first zero
    number:
      data.number.charAt(0) !== '0'
        ? `0${data.number}`
        : data.number,
  };
};

const normalizeEmail = (data) => {
  return {
    info: data.description,
    email: data.value,
  };
};

const normalizeWebPage = (data) => {
  return {
    info: data.value,
    url: data.url,
  };
};

const normalizeServiceHours = (data, idx) => {
  //   map hours from: { dayFrom: 'Monday', dayTo: 'Thursday', from: '08:00:00', to: '15:30:00' };
  //   to rendable form: { day: 'MA-TO', time: '8:00-15:30', };
  const openingHour = data.openingHour?.map((item) => ({
    day: [item.dayFrom, item.dayTo]
      .filter((subItem) => subItem)
      .map(translate)
      .join('-'),
    time: [item.from, item.to]
      .filter((subItem) => subItem)
      .map(prettifyTime)
      .join('–'),
  }));

  const validFromTo = [data.validFrom, data.validTo]
    .filter((item) => item)
    .map(prettifyDate)
    .join('–');

  return {
    key: idx,
    type: data.serviceHourType,
    info: data.additionalInformation?.find(finnish)?.value,
    isClosed: data.isClosed || false,
    isAlwaysOpen: data.isAlwaysOpen || false,
    isReservation: data.isReservation || false,
    openingHour,
    validFromTo,
  };
};

const getServiceChannelId = (data) =>
  data.serviceChannel?.id;

export const normalizeServiceChannelData = (data) => {
  const name =
    data.serviceChannelNames?.find(finnish)?.value || null;

  const summary =
    data.serviceChannelDescriptions?.find(
      finnishWithType('Summary'),
    )?.value || null;

  const description =
    data.serviceChannelDescriptions?.find(
      finnishWithType('Description'),
    )?.value || null;

  const serviceHours =
    data.serviceHours?.map(normalizeServiceHours) || [];

  const emails =
    data.emails?.filter(finnish).map(normalizeEmail) || [];

  const phoneNumbers =
    data.phoneNumbers
      ?.filter(finnish)
      .map(normalizePhoneNumber) || [];

  const webPages =
    data.webPages?.filter(finnish).map(normalizeWebPage) ||
    [];

  const addresses =
    data.addresses?.map(normalizeAddress) || [];

  return {
    id: data.id,
    name,
    summary,
    description,
    serviceHours,
    emails,
    phoneNumbers,
    webPages,
    addresses,
  };
};

export const normalizeOrganizationData = (
  organizationData,
) => {
  const data = organizationData.data;

  const name =
    data.organizationNames.find(finnishWithType('Name'))
      .value ||
    data.organizationNames.find(
      (item) => item.language === 'fi',
    ).value ||
    data.organizationNames[0]?.value ||
    '???';

  const summary =
    data.organizationDescriptions?.find(
      finnishWithType('Summary'),
    )?.value || null;
  const description =
    data.organizationDescriptions?.find(
      finnishWithType('Description'),
    )?.value || null;

  return {
    // ...data,
    name: name,
    summary: summary,
    description: description,
    businessCode: data.businessCode || null,
    webPages:
      data.webPages
        ?.filter(finnish)
        .map(normalizeWebPage) || null,
    emails:
      data.emails?.filter(finnish).map(normalizeEmail) ||
      null,
    phoneNumbers:
      data.phoneNumbers
        ?.filter(finnish)
        .map(normalizePhoneNumber) || null,
    addresses: data.addresses.map(normalizeAddress) || null,
  };
};

const normalizeServiceItem = (service) => ({
  id: service.id,
  keys: service.keys,
  // can service have multiple organizations? if not, can be changed from list to str
  organizations: [
    ...new Set(
      service.organizations.map(
        (org) => org.organization?.id,
      ),
    ),
  ],
  name: service.serviceNames[0].value,
  description: service.serviceDescriptions,
  serviceChannels: service.serviceChannels.map(
    getServiceChannelId,
  ),
  ontologies: service.ontologyTerms.map(
    (ontology) => ontology.name.find(finnish)?.value,
  ),
  showInOrganizationViews: true,
  serviceChargeType: service.serviceChargeType,
  serviceVoucherInUse: service.serviceVoucherInUse,
  languages: service.languages,
});

function filterItemFromitemList(
  filters,
  filterFunction,
  item,
) {
  // find services with 'our' ontology from the API response and append them
  // to updateServices variable which is defined in the parent function
  const tempKeys = [];
  Object.entries(filters).forEach((filter) => {
    if (filterFunction(filter[1])(item)) {
      tempKeys.push(filter[0]);
    }
  });

  const updateItem = {
    ...item,
    keys: tempKeys,
  };

  if (updateItem.keys.length > 0) {
    return normalizeServiceItem(updateItem);
  } else {
    return null;
  }
}

export function filterValidServicesFromAPIResponse(
  response,
) {
  if (!response.data.itemList) {
    return [];
  }

  // temporary variable where all the relevant services are pushed
  const validServices = [];
  let foundItem = null;

  for (let i = 0; i < response.data.itemList.length; i++) {
    const item = response.data.itemList[i];
    // filter items in this order and when the wanted service is found
    // append it to 'validServices' and jump to the next item in response data

    // ontologyTerms
    foundItem = filterItemFromitemList(
      ontologyFilters,
      inOntologies,
      item,
    );
    if (foundItem) {
      validServices.push(foundItem);
      foundItem = null;
      continue;
    }

    // lifeEvent
    foundItem = filterItemFromitemList(
      lifeEventsFilters,
      inLifeEvent,
      item,
    );
    if (foundItem) {
      validServices.push(foundItem);
      foundItem = null;
      continue;
    }

    // serviceClass
    foundItem = filterItemFromitemList(
      serviceClassFilters,
      inServiceClass,
      item,
    );
    if (foundItem) {
      validServices.push(foundItem);
      foundItem = null;
      continue;
    }

    // targetGroup
    foundItem = filterItemFromitemList(
      targetGroupFilters,
      inTargetGroup,
      item,
    );
    if (foundItem) {
      validServices.push(foundItem);
      foundItem = null;
      continue;
    }
  }
  return validServices;
}

// ensure proper handling of lists and convert markdown to html
export function prepareMarkdown(input) {
  return marked(
    input
      .replace(/(\n.*\n)(?![•|*])/g, '$1\n')
      .replace(/•/g, '* '),
  );
}

export const mapPharmaciesToMunicipality = (
  updateServices,
  municipalityCode,
) => {
  updateServices['Muut palvelut ja neuvonta'][
    'apteekit'
  ].data[0].serviceChannels =
    pharmacyMunicipalityMap[municipalityCode];
  return updateServices;
};
